import React, { useState, useEffect } from 'react';
import './ProductionTracking.css';

const API_URL = 'https://api.productfootprint.awstommy.com';

interface Recipe {
  id: string;
  name: string;
  totalEmissions?: number;
}

interface ProductionBatch {
  batchId: string;
  recipeId: string;
  recipeName: string;
  startTime: string;
  endTime: string;
  energyData: {
    totalKwh: number;
    readings: any[];
  };
  emissions: {
    ingredients: number;
    energy: number;
    total: number;
  };
}

interface ProductionTrackingProps {
  readOnly?: boolean;
}

export default function ProductionTracking({ readOnly = false }: ProductionTrackingProps) {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [batches, setBatches] = useState<ProductionBatch[]>([]);
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [isTracking, setIsTracking] = useState(false);
  const [trackingStartTime, setTrackingStartTime] = useState<Date | null>(null);
  const [useManualTime, setUseManualTime] = useState(false);
  const [manualStartTime, setManualStartTime] = useState('');
  const [manualEndTime, setManualEndTime] = useState('');
  const [useSimulatedData, setUseSimulatedData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadRecipes();
    loadBatches();
  }, []);

  const loadRecipes = async () => {
    try {
      const response = await fetch(`${API_URL}/recipe-storage`);
      const data = await response.json();
      setRecipes(data);
    } catch (error) {
      console.error('Failed to load recipes:', error);
    }
  };

  const loadBatches = async () => {
    try {
      const response = await fetch(`${API_URL}/production-batches`);
      const data = await response.json();
      setBatches(data);
    } catch (error) {
      console.error('Failed to load batches:', error);
    }
  };

  const handleStartTracking = () => {
    if (!selectedRecipeId) {
      alert('Please select a recipe first');
      return;
    }
    setIsTracking(true);
    setTrackingStartTime(new Date());
  };

  const handleStopTracking = async () => {
    if (!trackingStartTime) return;

    const endTime = new Date();
    await createBatch(trackingStartTime.toISOString(), endTime.toISOString());
    
    setIsTracking(false);
    setTrackingStartTime(null);
  };

  const handleManualBatch = async () => {
    if (!selectedRecipeId || !manualStartTime || !manualEndTime) {
      alert('Please fill in all fields');
      return;
    }

    // Convert local datetime-local input to ISO string
    const startISO = new Date(manualStartTime).toISOString();
    const endISO = new Date(manualEndTime).toISOString();
    
    console.log('Manual batch input:', { manualStartTime, manualEndTime });
    console.log('Converted to ISO:', { startISO, endISO });
    
    await createBatch(startISO, endISO);
    setManualStartTime('');
    setManualEndTime('');
  };

  const createBatch = async (startTime: string, endTime: string) => {
    setIsLoading(true);
    console.log('Creating batch with times:', { startTime, endTime });
    try {
      const response = await fetch(`${API_URL}/production-batches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipeId: selectedRecipeId,
          startTime,
          endTime,
          useSimulatedData
        })
      });

      const batch = await response.json();
      setBatches([batch, ...batches]);
      setSelectedRecipeId('');
      setUseSimulatedData(false);
    } catch (error) {
      console.error('Failed to create batch:', error);
      alert('Failed to create production batch');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (start: string, end: string) => {
    const diff = new Date(end).getTime() - new Date(start).getTime();
    const minutes = Math.floor(diff / 60000);
    return `${minutes} min`;
  };

  return (
    <div className="production-tracking">
      <h2>Production Tracking</h2>

      {!readOnly && (
        <div className="tracking-controls">
          <div className="control-section">
            <h3>New Production Batch</h3>
            
            <div className="form-group">
              <label>Recipe</label>
              <select 
                value={selectedRecipeId} 
                onChange={(e) => setSelectedRecipeId(e.target.value)}
                disabled={isTracking}
              >
                <option value="">Select a recipe...</option>
                {recipes.map(recipe => (
                  <option key={recipe.id} value={recipe.id}>
                    {recipe.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                <input 
                  type="checkbox" 
                  checked={useManualTime}
                  onChange={(e) => setUseManualTime(e.target.checked)}
                  disabled={isTracking}
                />
                Manual Time Entry
              </label>
            </div>

            <div className="form-group">
              <label>
                <input 
                  type="checkbox" 
                  checked={useSimulatedData}
                  onChange={(e) => setUseSimulatedData(e.target.checked)}
                />
                Use Simulated Data (700W for 18 min)
              </label>
            </div>

            {!useManualTime ? (
              <div className="realtime-controls">
                {!isTracking ? (
                  <button 
                    className="btn-start" 
                    onClick={handleStartTracking}
                    disabled={!selectedRecipeId}
                  >
                    ▶️ Start Batch
                  </button>
                ) : (
                  <div className="tracking-active">
                    <p>⏱️ Tracking in progress...</p>
                    <p>Started: {trackingStartTime?.toLocaleTimeString()}</p>
                    <button className="btn-stop" onClick={handleStopTracking}>
                      ⏹️ Stop Batch
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="manual-controls">
                <div className="form-group">
                  <label>Start Time</label>
                  <input 
                    type="datetime-local" 
                    value={manualStartTime}
                    onChange={(e) => setManualStartTime(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <input 
                    type="datetime-local" 
                    value={manualEndTime}
                    onChange={(e) => setManualEndTime(e.target.value)}
                  />
                </div>
                <button 
                  className="btn-create" 
                  onClick={handleManualBatch}
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : 'Create Batch'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="batches-list">
        <h3>Production Batches ({batches.length})</h3>
        {batches.length === 0 ? (
          <p>No production batches yet. Create your first batch above!</p>
        ) : (
          <div className="batches-grid">
            {batches.map(batch => (
              <div key={batch.batchId} className="batch-card">
                <h4>{batch.recipeName}</h4>
                <div className="batch-details">
                  <p><strong>Duration:</strong> {formatDuration(batch.startTime, batch.endTime)}</p>
                  <p><strong>Energy:</strong> {batch.energyData.totalKwh.toFixed(3)} kWh</p>
                  <div className="emissions-breakdown">
                    <p><strong>Carbon Footprint:</strong></p>
                    <ul>
                      <li>Ingredients: {batch.emissions.ingredients.toFixed(3)} kg CO₂e</li>
                      <li>Energy: {batch.emissions.energy.toFixed(3)} kg CO₂e</li>
                      <li className="total">Total: {batch.emissions.total.toFixed(3)} kg CO₂e</li>
                    </ul>
                  </div>
                  <p className="batch-time">{new Date(batch.startTime).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
